export interface FleetSummary {
  specialPriceSetId: number;
  name: string;
  isVisible: boolean;
  fleetAuthType: number | null;
  fleetAuthSubType: number | null;
  hasDocument: boolean;
  matrixRuleCount: number;
  skuExceptionCount: number;
  sellerCount: number;
  franchiseCount?: number;
  isComplete: boolean;
  missingRequirements: string[];
}

export interface FleetListResponse {
  items: FleetSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface FleetOverview {
  name: string;
  isVisible: boolean;
  hasDynamicPricing: boolean;
  callCentreNumber: string | null;
  callCentreEmail: string | null;
  customStockGroupId: number | null;
  fleetAuthType: number | null;
  fleetAuthSubType: number | null;
}

export interface FleetMatrixRule {
  specialPriceMatrixId: number | null;
  brandId: number;
  brandName: string | null;
  rim: number | null;
  isCommercial: boolean;
  priceMatrixMethodId: number;
  method: string | null;
  value: number;
  selected?: boolean;
}

export interface FleetSkuException {
  specialPriceItemId: number | null;
  msfid: number;
  stockCode: string | null;
  description: string | null;
  price: number | null;
  priceMatrixMethodId: number | null;
  method: string | null;
  priceMatrixMethodValue: number | null;
  selected?: boolean;
}

export interface FleetSeller {
  specialPriceSetSellerId: number | null;
  sellerId: number;
  sellerName: string | null;
  franchiseId: number | null;
  merchantNumber: string | null;
  isActive: boolean;
  isSupported: boolean;
  selected?: boolean;
}

export interface FleetAuthRequirement {
  fleetAuthFieldRequirementId: number | null;
  fleetAuthFieldId: number | null;
  fleetAuthFieldName: string;
  tooltipText: string | null;
  dataType: number;
  displayRank: number | null;
  modelField: string | null;
  isRequired: boolean;
  isNew: boolean;
  selected?: boolean;
}

export interface FleetConfiguration {
  specialPriceSetId: number | null;
  versionHash: string | null;
  overview: FleetOverview;
  franchiseIds: number[];
  sellers: FleetSeller[];
  matrixRules: FleetMatrixRule[];
  skuExceptions: FleetSkuException[];
  authFields: FleetAuthRequirement[];
  documentBase64?: string | null;
  issues: FleetConfigurationIssue[];
}

export interface FleetConfigurationIssue {
  code: string;
  message: string;
  section: 'overview' | 'pricingRules' | 'stockCodeExceptions' | 'availability' | 'authorisation' | 'document';
  rowId: number | null;
}

export interface LookupItem {
  id: number;
  name: string;
  description: string | null;
}

export interface AuthFieldLookup {
  fleetAuthFieldId: number;
  fleetAuthFieldName: string;
  tooltipText: string | null;
  dataType: number;
  displayRank: number | null;
  modelField: string | null;
}

export interface FleetAuthFieldUpdate {
  fleetAuthFieldName: string;
  tooltipText: string | null;
}

export interface FleetLookups {
  priceMethods: LookupItem[];
  franchises: LookupItem[];
  customStockGroups: LookupItem[];
  authFields: AuthFieldLookup[];
  rimSizes: number[];
}

export interface SearchLookupResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface BrandLookup {
  brandId: number;
  name: string;
}

export interface TyreLookup {
  msfid: number;
  stockCode: string;
  description: string;
  brand: string;
  rim: number | null;
}

export interface SellerLookup {
  sellerId: number;
  name: string;
  franchiseId: number;
  city: string | null;
  province: string | null;
}

export interface MerchantNumberLookup {
  merchantNumber: string;
}

export interface FleetSaveResponse {
  specialPriceSetId: number;
  versionHash: string;
  message: string;
}

export function emptyFleet(): FleetConfiguration {
  return {
    specialPriceSetId: null,
    versionHash: null,
    overview: {
      name: '',
      isVisible: false,
      hasDynamicPricing: false,
      callCentreNumber: null,
      callCentreEmail: null,
      customStockGroupId: null,
      fleetAuthType: 2,
      fleetAuthSubType: 2
    },
    franchiseIds: [],
    sellers: [],
    matrixRules: [],
    skuExceptions: [],
    authFields: [],
    documentBase64: null,
    issues: []
  };
}
